import { PayloadAction, createAction, createReducer } from "@reduxjs/toolkit";
import {
  ActivityMenuItem,
  CalculationTestData,
  CustomTestData,
} from "Hub/pages/TaskDetails/types";
import { Methodology } from "types/Methodology";

import {
  ClassificationMapped,
  ParametersMappedData,
  TaskDetails,
  VarianceChecksList,
} from "API/Endpoints/Tasks/payloads";

import { STORES } from "store/types";

import {
  ErrorProps,
  FilesUploadData,
  MissingEntity,
  ReloadTrigger,
  Tab,
  TabVisibility,
} from "./types";
import { selectMenuItem } from "./utils";

export interface TaskDetailsState {
  currentTaskDetailsData: TaskDetails | null;
  selectedMenuItemId: string;
  menuItems: ActivityMenuItem[];
  missingDataMethodologyTab: Tab;
  encodedCalculationTestData: CalculationTestData | null;
  customFileData: CustomTestData | null;
  reportedEmissionsData: CalculationTestData | null;
  isTaskClaimedByCurrentUser: boolean;
  filesUploadData: FilesUploadData | null;
  parametersMappedData: ParametersMappedData | null;
  classifiedData: ClassificationMapped | null;
  varianceChecksData: VarianceChecksList | null;
  submitEnabled: boolean;
  errorData: ErrorProps | null;
  tabVisibility: TabVisibility;
  methodologies: Methodology[];
  isMenuFocus: boolean;
  missingEntities: MissingEntity[];
  busyMessages: string[];
  reloadTrigger: ReloadTrigger | undefined;
}

// define action types
export const initialize = createAction(
  `${STORES.hub}/${STORES.taskDetails}/initialize`
);

export const reload = createAction<ReloadTrigger | undefined>(
  `${STORES.hub}/${STORES.taskDetails}/reload`
);

export const setCurrentTaskDetailsData = createAction<TaskDetails | null>(
  `${STORES.hub}/${STORES.taskDetails}/setCurrentTaskDetailsData`
);

export const setSelectedMenuItemId = createAction<string>(
  `${STORES.hub}/${STORES.taskDetails}/setSelectedMenuItemId`
);

export const setMenuItems = createAction<ActivityMenuItem[]>(
  `${STORES.hub}/${STORES.taskDetails}/setMenuItems`
);

export const setMissingDataMethodologyTab = createAction<Tab>(
  `${STORES.hub}/${STORES.taskDetails}/setMissingDataMethodologyTab`
);

export const setEncodedCalculationTestData =
  createAction<CalculationTestData | null>(
    `${STORES.hub}/${STORES.taskDetails}/setEncodedCalculationTestData`
  );

export const setCustomFileData = createAction<CustomTestData | null>(
  `${STORES.hub}/${STORES.taskDetails}/setCustomFileData`
);

export const setReportedEmissionsData =
  createAction<CalculationTestData | null>(
    `${STORES.hub}/${STORES.taskDetails}/setReportedEmissionsData`
  );

export const setIsTaskClaimedByCurrentUser = createAction<boolean>(
  `${STORES.hub}/${STORES.taskDetails}/setIsTaskClaimedByCurrentUser`
);

export const setFilesUploadData = createAction<FilesUploadData | null>(
  `${STORES.hub}/${STORES.taskDetails}/setFilesUploadData`
);

export const setParametersMappedData =
  createAction<ParametersMappedData | null>(
    `${STORES.hub}/${STORES.taskDetails}/setParametersMappedData`
  );

export const setClassifiedData = createAction<ClassificationMapped | null>(
  `${STORES.hub}/${STORES.taskDetails}/setClassifiedData`
);

export const setVarianceChecksData = createAction<VarianceChecksList | null>(
  `${STORES.hub}/${STORES.taskDetails}/setVarianceChecksData`
);

export const setSubmitEnabled = createAction<boolean>(
  `${STORES.hub}/${STORES.taskDetails}/setSubmitEnabled`
);

export const setErrorData = createAction<ErrorProps | null>(
  `${STORES.hub}/${STORES.taskDetails}/setErrorData`
);

export const setTabVisibility = createAction<TabVisibility>(
  `${STORES.hub}/${STORES.taskDetails}/setTabVisibility`
);

export const setMethodologies = createAction<Methodology[]>(
  `${STORES.hub}/${STORES.taskDetails}/setMethodologies`
);

export const setIsMenuFocus = createAction<boolean>(
  `${STORES.hub}/${STORES.taskDetails}/setIsMenuFocus`
);

export const setMissingEntities = createAction<MissingEntity[]>(
  `${STORES.hub}/${STORES.taskDetails}/setMissingEntities`
);

export const setBusyMessages = createAction<string[]>(
  `${STORES.hub}/${STORES.taskDetails}/setBusyMessages`
);

// create reducer
const taskDetailsReducer = createReducer<TaskDetailsState>(
  {
    currentTaskDetailsData: null,
    selectedMenuItemId: "",
    menuItems: [],
    missingDataMethodologyTab: Tab.MISSING_DATA,
    encodedCalculationTestData: null,
    customFileData: null,
    isTaskClaimedByCurrentUser: false,
    reportedEmissionsData: null,
    filesUploadData: null,
    parametersMappedData: null,
    classifiedData: null,
    varianceChecksData: null,
    submitEnabled: false,
    errorData: null,
    tabVisibility: TabVisibility.HIDDEN,
    methodologies: [],
    isMenuFocus: false,
    missingEntities: [],
    busyMessages: [],
    reloadTrigger: undefined,
  },
  (builder) => {
    builder.addCase(initialize, (state) => initializeState(state));

    builder.addCase(
      reload,
      (state, action: PayloadAction<ReloadTrigger | undefined>) => {
        reloadState(state);
        state.reloadTrigger = action.payload;
      }
    );

    builder.addCase(
      setCurrentTaskDetailsData,
      (state, action: PayloadAction<TaskDetails | null>) => {
        state.currentTaskDetailsData = action.payload;
      }
    );

    builder.addCase(
      setSelectedMenuItemId,
      (state, action: PayloadAction<string>) => {
        state.selectedMenuItemId = action.payload;
      }
    );

    builder.addCase(
      setMenuItems,
      (state, action: PayloadAction<ActivityMenuItem[]>) => {
        state.menuItems = action.payload;
        selectMenuItem(state, action.payload);
      }
    );

    builder.addCase(
      setMissingDataMethodologyTab,
      (state, action: PayloadAction<Tab>) => {
        state.missingDataMethodologyTab = action.payload;
      }
    );

    builder.addCase(
      setIsTaskClaimedByCurrentUser,
      (state, action: PayloadAction<boolean>) => {
        state.isTaskClaimedByCurrentUser = action.payload;
      }
    );

    builder.addCase(
      setEncodedCalculationTestData,
      (state, action: PayloadAction<CalculationTestData | null>) => {
        state.encodedCalculationTestData = action.payload;
      }
    );

    builder.addCase(
      setFilesUploadData,
      (state, action: PayloadAction<FilesUploadData | null>) => {
        state.filesUploadData = action.payload;
      }
    );

    builder.addCase(
      setParametersMappedData,
      (state, action: PayloadAction<ParametersMappedData | null>) => {
        state.parametersMappedData = action.payload;
      }
    );

    builder.addCase(
      setClassifiedData,
      (state, action: PayloadAction<ClassificationMapped | null>) => {
        state.classifiedData = action.payload;
      }
    );

    builder.addCase(
      setCustomFileData,
      (state, action: PayloadAction<CustomTestData | null>) => {
        state.customFileData = action.payload;
      }
    );

    builder.addCase(
      setReportedEmissionsData,
      (state, action: PayloadAction<CalculationTestData | null>) => {
        state.reportedEmissionsData = action.payload;
      }
    );

    builder.addCase(
      setVarianceChecksData,
      (state, action: PayloadAction<VarianceChecksList | null>) => {
        state.varianceChecksData = action.payload;
      }
    );

    builder.addCase(
      setSubmitEnabled,
      (state, action: PayloadAction<boolean>) => {
        state.submitEnabled = action.payload;
      }
    );

    builder.addCase(
      setErrorData,
      (state, action: PayloadAction<ErrorProps | null>) => {
        state.errorData = action.payload;
      }
    );

    builder.addCase(
      setTabVisibility,
      (state, action: PayloadAction<TabVisibility>) => {
        state.tabVisibility = action.payload;
      }
    );

    builder.addCase(
      setMethodologies,
      (state, action: PayloadAction<Methodology[]>) => {
        state.methodologies = action.payload;
      }
    );

    builder.addCase(setIsMenuFocus, (state, action: PayloadAction<boolean>) => {
      state.isMenuFocus = action.payload;
    });

    builder.addCase(
      setMissingEntities,
      (state, action: PayloadAction<MissingEntity[]>) => {
        state.missingEntities = action.payload;
      }
    );

    builder.addCase(
      setBusyMessages,
      (state, action: PayloadAction<string[]>) => {
        state.busyMessages = action.payload;
      }
    );
  }
);

const initializeState = (state: TaskDetailsState) => {
  reloadState(state);
  state.missingDataMethodologyTab = Tab.MISSING_DATA;
  state.reloadTrigger = undefined;
  state.selectedMenuItemId = "";
  state.encodedCalculationTestData = null;
  state.reportedEmissionsData = null;
  state.customFileData = null;
};

const reloadState = (state: TaskDetailsState) => {
  state.currentTaskDetailsData = null;
  state.menuItems = [];
  state.isTaskClaimedByCurrentUser = false;
  state.filesUploadData = null;
  state.parametersMappedData = null;
  state.classifiedData = null;
  state.varianceChecksData = null;
  state.submitEnabled = false;
  state.errorData = null;
  state.tabVisibility = TabVisibility.HIDDEN;
  state.methodologies = [];
  state.isMenuFocus = false;
  state.missingEntities = [];
};

export default taskDetailsReducer;
